import { useEffect, useState } from "react";
import { WeekNavigator } from "../WeekNavigator";
import { gymClient, ApiError } from "../../apiClient/gymClient";
import { addDays, formatFullDate, parseBookingSlotId, startOfToday, toISODate } from "../../utils/dates";
import type { BookingWithAttendee } from "../../types/gym.types";

function AdminBookingRow({
  booking,
  onCancelled,
}: {
  booking: BookingWithAttendee;
  onCancelled: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { startTime } = parseBookingSlotId(booking.slotId);

  async function handleCancel() {
    setBusy(true);
    setError(null);
    try {
      await gymClient.adminCancelBooking(booking.id);
      onCancelled();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't cancel this session.");
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <li className="booking-row">
      <span className="booking-time">{startTime}</span>
      <span className="attendee">
        <span className="attendee-name">{booking.fullName}</span>
        <span className="attendee-mobile">{booking.mobileNumber}</span>
      </span>

      {booking.status === "confirmed" ? (
        confirming ? (
          <div className="cancel-confirm">
            <span>Delete this booking?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={busy}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleCancel} disabled={busy}>
              {busy ? "Deleting…" : "Yes, delete"}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirming(true)}>
            Delete booking
          </button>
        )
      ) : (
        <span className={`status-badge ${booking.status === "attended" ? "status-badge--attended" : "status-badge--no-show"}`}>
          {booking.status === "attended" ? "Attended" : booking.status === "no_show" ? "No-show" : booking.status}
        </span>
      )}
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

export function AdminBookingsTab() {
  const [date, setDate] = useState(() => toISODate(startOfToday()));
  const [bookings, setBookings] = useState<BookingWithAttendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    gymClient
      .getAdminBookings(date)
      .then(setBookings)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load bookings."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, [date]);

  return (
    <div>
      <div className="schedule-toolbar">
        <WeekNavigator
          label={formatFullDate(date)}
          onPrev={() => setDate((d) => toISODate(addDays(new Date(`${d}T00:00:00`), -1)))}
          onNext={() => setDate((d) => toISODate(addDays(new Date(`${d}T00:00:00`), 1)))}
          canPrev
          canNext
        />
      </div>

      {error ? (
        <div className="state-panel">
          <h2>Couldn&rsquo;t load bookings</h2>
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : bookings.length === 0 ? (
        <div className="state-panel">
          <p>No bookings for this day.</p>
        </div>
      ) : (
        <div className="bookings-day">
          <ul className="bookings-day-items">
            {bookings.map((b) => (
              <AdminBookingRow key={b.id} booking={b} onCancelled={refresh} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
